import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the HiringRequestsContext
const MyHiringRequestsContext = createContext();

// Custom hook to use the context
export const useMyHiringRequests = () => useContext(MyHiringRequestsContext);

// Provider component
export const MyHiringRequestsProvider = ({ children }) => {
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = "https://hrmsnew.prepstripe.com/api/";
  const token = getToken();

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [API_URL, token]
  );

  // Fetch all hiring requests
  const fetchMyHiringRequests = async (page = 1) => {
    try {
      setLoading(true);
      setError(null); // Reset error state
      const response = await axiosInstance.get(`my-hiring-requests?page=${page}`);
      await new Promise((r) => setTimeout(r, 1000));
      setRequests(response.data.data.data);
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
    } catch (err) {
      console.error("Error fetching my hiring requests:", err);
      setError(err.response?.data?.message || "Failed to fetch my hiring requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyHiringRequests();
  }, []);

  const value = useMemo(
    () => ({
      requests,
      loading,
      error,
      pagination,
      fetchMyHiringRequests,
    }),
    [requests, loading, error]
  );

  return <MyHiringRequestsContext.Provider value={value}>{children}</MyHiringRequestsContext.Provider>;
};
